import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { devLog, logError } from "../utils/logger";

/**
 * Hook for managing user's personal notes (MyNotesPage)
 * Supports filtering by visibility: 'all', 'public', 'friends', 'private'
 */
export function useMyNotes(initialFilter = "all", limit = 20) {
  // Get current user from auth context
  const { user } = useAuth();

  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState({ all: 0, public: 0, private: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  devLog("useMyNotes hook:", {
    initialFilter,
    api,
    userId: user?.id,
  });

  if (!api) {
    logError("api is not defined!");
    throw new Error("api is not available");
  }

  // Fetch notes based on current filter
  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setOffset(0);
      const response = await api.getMyNotes({ filter, limit, offset: 0 });
      setNotes(response.data);
      setHasMore(response.hasMore);
    } catch (err) {
      setError(err.message || "Failed to load notes");
      logError("Failed to fetch my notes:", err);
    } finally {
      setLoading(false);
    }
  }, [filter, limit]);

  // Fetch stats separately (lighter weight, can be cached)
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await api.getMyNotesStats();
      setStats(response.data);
    } catch (err) {
      logError("Failed to fetch note stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Fetch stats once on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Fetch notes when filter changes
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Load more notes (pagination)
  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;

    try {
      setLoadingMore(true);
      const newOffset = offset + limit;
      const response = await api.getMyNotes({
        filter,
        limit,
        offset: newOffset,
      });
      setNotes((prev) => [...prev, ...response.data]);
      setHasMore(response.hasMore);
      setOffset(newOffset);
    } catch (err) {
      setError(err.message || "Failed to load more notes");
      logError("Failed to load more notes:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, offset, filter, limit]);

  // Create a new note
  const createNote = useCallback(
    async (noteData) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      try {
        const response = await api.createNote(noteData);
        const newNote = response.data;

        // Optimistic update: add new note to the beginning of the list
        setNotes((prev) => [newNote, ...prev]);

        // Increment offset to account for the new note in pagination
        setOffset((prev) => prev + 1);

        // Refresh stats only (not the entire notes list)
        await fetchStats();

        return newNote;
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [user, fetchStats],
  );

  // Update an existing note
  const updateNote = useCallback(
    async (id, updates) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      try {
        const response = await api.updateNote(id, updates);
        const updatedNote = response.data;

        // Optimistic update: update the note in the list
        setNotes((prev) =>
          prev.map((note) => (note.id === id ? updatedNote : note)),
        );

        // Refresh stats only (visibility might have changed)
        await fetchStats();

        return updatedNote;
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [user, fetchStats],
  );

  // Delete a note
  const deleteNote = useCallback(
    async (id) => {
      try {
        await api.deleteNote(id);

        // Optimistic update: remove the note from the list
        setNotes((prev) => prev.filter((note) => note.id !== id));

        // Decrement offset to account for the deleted note in pagination
        setOffset((prev) => Math.max(0, prev - 1));

        // Refresh stats only
        await fetchStats();
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [fetchStats],
  );

  /**
   * Fetch a single note by ID with fresh data from backend
   * Useful for refreshing note data when opening modal
   * Also updates the note in the notes list if it exists
   * @param {string} noteId - The ID of the note to fetch
   * @returns {Promise<Object>} The fresh note data
   */
  const fetchNote = useCallback(async (noteId) => {
    try {
      setLoadingMore(true);
      setError(null);
      const response = await api.getNote(noteId);

      // Update the note in the list if it exists
      setNotes((prev) =>
        prev.map((note) => (note.id === noteId ? response.data : note)),
      );

      return response.data;
    } catch (err) {
      logError("Failed to fetch note:", err);
      setError(err.message || "Failed to load note");
      throw err;
    } finally {
      setLoadingMore(false);
    }
  }, []);

  return {
    notes,
    stats,
    statsLoading,
    loading,
    loadingMore,
    error,
    filter,
    setFilter,
    hasMore,
    loadMore,
    refetch: fetchNotes,
    refetchStats: fetchStats,
    createNote,
    updateNote,
    deleteNote,
    fetchNote,
  };
}
